import api from "../../../services/baseapi";
import {
  CardDimensionApi,
  CardMetricApi,
  DashboardAcceptedApi,
  DashboardCardApi,
} from "../../../types/types";
import type { GenerateReportResponse } from "../../../types/report";

export type UploadInternalDashboardPayload = {
  projectId: string;
  file: File;
  name?: string;
};

export type UploadInternalDashboardResponse = {
  dashboard_id: string;
  document_type: string;
  name: string;
  columns: string[];
  row_count: number;
  cards_generated: number;
  message: string;
};

export async function uploadInternalDashboard({
  projectId,
  file,
  name,
}: UploadInternalDashboardPayload): Promise<UploadInternalDashboardResponse> {
  const formData = new FormData();
  formData.append("project_id", projectId);
  formData.append("file", file);

  if (name?.trim()) {
    formData.append("name", name.trim());
  }

  const response = await api.post<UploadInternalDashboardResponse>(
    `${process.env.REACT_APP_TYPE_API_URL}/dashboards/add-data/internal`,
    formData,
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  return response.data;
}

export type UploadExternalDashboardPayload = {
  projectId: string;
  externalUrl: string;
  name: string;
};

export type UploadExternalDashboardResponse = {
  dashboard_id: string;
  document_type: string;
  name: string;
  columns: string[] | null;
  row_count: number | null;
  cards_generated: number;
  message: string;
};

export async function uploadExternalDashboard({
  projectId,
  externalUrl,
  name,
}: UploadExternalDashboardPayload): Promise<UploadExternalDashboardResponse> {
  const formData = new FormData();
  formData.append("project_id", projectId);
  formData.append("external_url", externalUrl);
  formData.append("name", name);

  const response = await api.post<UploadExternalDashboardResponse>(
    `/dashboards/add-data/external`,
    formData,
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  return response.data;
}

export const acceptDashboardTemplate = async (
  dashboardId: string,
): Promise<DashboardAcceptedApi> => {
  const { data } = await api.post(`/dashboards/${dashboardId}/accept-template`);
  return data;
};

export const getTabCards = async (
  tabId: string,
): Promise<DashboardCardApi[]> => {
  const { data } = await api.get(`/tabs/${tabId}/cards`);
  return data;
};

export const getCardData = async (
  cardId: string,
  payload: {
    filters?: Record<string, any>;
    metrics?: CardMetricApi[];
    dimensions?: CardDimensionApi[];
    time_range?: Record<string, any>;
    time_granularity?: string;
    comparison_period?: Record<string, any>;
    drill_level?: string;
    drill_column?: string;
    num_bins?: number;
  },
) => {
  const { data } = await api.post(`/cards/${cardId}/data`, payload);
  return data;
};
export const updateDashboard = async (
  dashboardId: string,
  payload: {
    name: string;
    header_config: Record<string, any>;
    footer_config: Record<string, any>;
  },
) => {
  const { data } = await api.put(`/dashboards/${dashboardId}`, payload);
  return data;
};

// export const updateDashboardHeader = async (
//   dashboardId: string,
//   payload: {
//     title: string;
//     subtitle: string;
//     font_color: string;
//     font_size: number;
//     font_type: string;
//     padding_line_height: string;
//     padding_left: string;
//     padding_right: string;
//     image_url: string;
//     image_alignment: string;
//     background_color: string;
//     border_color: string;
//     border_radius: number;
//     border_stroke: number;
//   },
// ) => {
//   const { data } = await api.put(`/dashboards/${dashboardId}/header`, payload);
//   return data;
// };

// export const updateDashboardFooter = async (
//   dashboardId: string,
//   payload: {
//     title: string;
//     font_color: string;
//     font_size: number;
//     font_type: string;
//     padding_line_height: string;
//     padding_left: string;
//     padding_right: string;
//     image_url: string;
//     image_alignment: string;
//     background_color: string;
//     border_color: string;
//   },
// ) => {
//   const { data } = await api.put(`/dashboards/${dashboardId}/footer`, payload);
//   return data;
// };
export const getDashboards = async ({
  projectId,
  search,
  searchCreator,
  documentType,
  dateFrom,
  dateTo,
  skip = 0,
  limit = 100,
}: {
  projectId: string;
  search?: string;
  searchCreator?: string;
  documentType?: string;
  dateFrom?: string;
  dateTo?: string;
  skip?: number;
  limit?: number;
}) => {
  const params: Record<string, any> = {
    project_id: projectId,
    skip,
    limit,
  };

  if (search) params.search = search;
  if (searchCreator) params.search_creator = searchCreator;
  if (documentType) params.document_type = documentType;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const { data } = await api.get("/dashboards", { params });
  return data;
};
export const getDashboardById = async (dashboardId: string) => {
  const { data } = await api.get(`/dashboards/${dashboardId}`);
  return data;
};
export const getDashboardColumns = async (dashboardId: string) => {
  const { data } = await api.get(`/dashboards/${dashboardId}/columns`);
  return data;
};

export const addTextBoxCard = async (tabId: string, payload: any) => {
  const { data } = await api.post(`/tabs/${tabId}/cards/text-box`, payload);
  return data;
};

// export const addImageCard = async (tabId: string, payload: any) => {
//   const formData = new FormData();

//   Object.entries(payload).forEach(([key, value]) => {
//     if (value !== undefined && value !== null) {
//       formData.append(key, value as any);
//     }
//   });

//   const { data } = await api.post(`/tabs/${tabId}/cards/image`, formData, {
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });

//   return data;
// };

export const addInsightCard = async (tabId: string, payload: any) => {
  const { data } = await api.post(`/tabs/${tabId}/cards/insight`, payload);
  return data;
};

export const createDashboardTab = async (
  dashboardId: string,
  payload: { name: string; tab_filters?: Record<string, any> },
) => {
  const response = await api.post(`/dashboards/${dashboardId}/tabs`, payload);
  return response.data;
};

// export const uploadDashboardImage = async (dashboardId: string, file: File) => {
//   const formData = new FormData();
//   formData.append("image", file);

//   const response = await api.post(
//     `/dashboards/${dashboardId}/upload-image`,
//     formData,
//     {
//       headers: {
//         "Content-Type": "multipart/form-data",
//       },
//     },
//   );

//   return response.data;
// };

export const addImageCard = async (tabId: string, payload: any) => {
  const response = await api.post(`/tabs/${tabId}/cards/image`, payload);
  return response.data;
};

export const updateDashboardHeader = async (
  dashboardId: string,
  payload: Record<string, any>,
) => {
  const response = await api.put(`/dashboards/${dashboardId}/header`, payload);
  return response.data;
};

export const updateDashboardFooter = async (
  dashboardId: string,
  payload: Record<string, any>,
) => {
  const response = await api.put(`/dashboards/${dashboardId}/footer`, payload);
  return response.data;
};

export const uploadDashboardImage = async (dashboardId: string, file: File) => {
  const formData = new FormData();
  formData.append("image", file);

  const response = await api.post(
    `/dashboards/${dashboardId}/upload-image`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const deleteDashboard = async (dashboardId: string) => {
  const { data } = await api.delete(`/dashboards/${dashboardId}`);
  return data;
};

export const deleteDashboardCard = async (cardId: string) => {
  const { data } = await api.delete(`/cards/${cardId}`);
  return data;
};

export const updateDashboardTab = async (
  tabId: string,
  payload: { name?: string; tab_filters?: Record<string, any> },
) => {
  const { data } = await api.put(`/tabs/${tabId}`, payload);
  return data;
};

export const deleteDashboardTab = async (tabId: string) => {
  const { data } = await api.delete(`/tabs/${tabId}`);
  return data;
};
export const updateDashboardCard = async (
  cardId: string,
  payload: Record<string, any>,
) => {
  const { data } = await api.put(`/cards/${cardId}`, payload);
  return data;
};
export const exportDashboard = async (
  dashboardId: string,
  format: "csv" | "xlsx" | "pdf" = "csv",
  tabId?: string,
) => {
  const response = await api.get(`/dashboards/${dashboardId}/export`, {
    params: {
      format,
      ...(tabId ? { tab_id: tabId } : {}),
    },
    responseType: "blob",
  });

  return response.data;
};
export const exportDashboardFile = async (
  dashboardId: string,
  format: "csv" | "xlsx" = "csv",
) => {
  const response = await api.get(`/dashboards/${dashboardId}/export`, {
    params: { format },
    responseType: "blob",
  });

  return response.data;
};

// ── Share endpoints ──────────────────────────────────────────────────────────

export type ShareInfo = {
  id: string;
  dashboard_id: string;
  share_token: string;
  shared_url: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
};

export const getShareInfo = async (dashboardId: string): Promise<ShareInfo> => {
  const { data } = await api.get(`/dashboards/${dashboardId}/share`);
  return data;
};

export const createShare = async (dashboardId: string): Promise<ShareInfo> => {
  const { data } = await api.post(`/dashboards/${dashboardId}/share`);
  return data;
};

export const toggleShare = async (
  dashboardId: string,
  is_active: boolean,
): Promise<ShareInfo> => {
  const { data } = await api.patch(`/dashboards/${dashboardId}/share`, {
    is_active,
  });
  return data;
};

export const regenerateShare = async (
  dashboardId: string,
): Promise<ShareInfo> => {
  const { data } = await api.post(
    `/dashboards/${dashboardId}/share/regenerate`,
  );
  return data;
};

export const getPublicDashboard = async (
  shareToken: string,
): Promise<DashboardAcceptedApi> => {
  const { data } = await api.get(`/public/dashboards/${shareToken}`);
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────

export const generateDashboardReport = async (
  dashboardId: string,
  payload: {
    report_type: string;
    tab_id?: string;
    filters?: Record<string, any>;
    date_range?: Record<string, any>;
    dashboard_data?: Record<string, any>;
  },
): Promise<GenerateReportResponse> => {
  const { data } = await api.post(
    `/dashboards/${dashboardId}/reports/generate`,
    payload,
  );
  return data;
};
